import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CartSelection, PlatformKey, FormatType } from "@/features/blogger-card";

export type CampaignType = "seeding" | "native";
export type Geo = { mode: "all" | "custom"; regions: string[] };
export type PlacementDate = { date: string; date_to: string };

export type CreativeVariant = {
  id: string;
  name: string;
  tone: string;
  text: string;
  link: string;
  erid?: string;
  eridOnImage?: boolean;
  media: { id: string; kind: "image" | "video"; url: string }[];
  // native brief fields
  forbidden?: string;
};

export type DraftCampaign = {
  productUrl: string;
  productAutofilled: boolean;
  productName: string;
  productCategory: string;
  productCategoryCustom: string;
  productType: string;
  productTypeCustom: string;
  productDesc: string;
  geo: Geo;
  budget: number;
  selectedBloggerIds: string[];
  bloggerFormats: Record<string, CartSelection[]>;
  creativeChannelMap: Record<string, string>;
  utmPerBlogger: Record<string, string>;
  placementDates: Record<string, PlacementDate>;
  creative: {
    seedingText: string;
    nativeBrief: { task: string; tone: string; forbidden: string; hashtags: string };
    abVariant: string | null;
  };
  variants: CreativeVariant[];
  advertiserDataSaved: boolean;
};

export type Branch = {
  campaignType: CampaignType;
  bloggersTab: "recommended" | "catalog" | "adjacent";
  budgetMode: "auto" | "manual";
  payment: "card" | "invoice";
  kanbanView: "kanban" | "table";
};

const initialDraft: DraftCampaign = {
  productUrl: "",
  productAutofilled: false,
  productName: "",
  productCategory: "",
  productCategoryCustom: "",
  productType: "service",
  productTypeCustom: "",
  productDesc: "",
  geo: { mode: "all", regions: [] },
  // Budget is 0 until the user explicitly picks one through the
  // "Как определить бюджет?" popover on the Bloggers step. Downstream code
  // (forecast, cart progress bar) special-cases 0 as "not set yet".
  budget: 0,
  selectedBloggerIds: [],
  bloggerFormats: {},
  creativeChannelMap: {},
  utmPerBlogger: {},
  placementDates: {},
  creative: {
    seedingText: "",
    nativeBrief: { task: "", tone: "", forbidden: "", hashtags: "" },
    abVariant: null,
  },
  variants: [],
  advertiserDataSaved: false,
};

const initialBranch: Branch = {
  campaignType: "seeding",
  bloggersTab: "recommended",
  budgetMode: "auto",
  payment: "card",
  kanbanView: "table",
};

type State = {
  draft: DraftCampaign;
  branch: Branch;
  autofillState: "idle" | "fetching" | "done";
  activeChatBloggerId: string | null;
  activeCampaignId: string | null;
  /** Backend id of the saved draft Campaign — set after the first /campaigns/draft save. */
  draftCampaignId: string | null;
};

type Actions = {
  patchDraft: (patch: Partial<DraftCampaign>) => void;
  setBranch: <K extends keyof Branch>(key: K, value: Branch[K]) => void;
  toggleBlogger: (id: string) => void;
  addBloggerFormat: (bloggerId: string, sel: CartSelection) => void;
  removeBloggerFormat: (bloggerId: string, platform: PlatformKey, type: FormatType) => void;
  assignCreative: (bloggerId: string, variantId: string) => void;
  updateBloggerFormat: (bloggerId: string, platform: PlatformKey, type: FormatType, entry: { id: string; name: string; price: number; cpv: number }) => void;
  setBloggerUtm: (bloggerId: string, utm: string) => void;
  setPlacementDate: (bloggerId: string, date: string, dateTo: string) => void;
  setBudget: (n: number) => void;
  setGeo: (geo: Geo) => void;
  setVariants: (vs: CreativeVariant[]) => void;
  setAutofillState: (s: State["autofillState"]) => void;
  setActiveChat: (id: string | null) => void;
  setActiveCampaign: (id: string | null) => void;
  setDraftCampaignId: (id: string | null) => void;
  loadDraftFromServer: (snapshot: { draft: DraftCampaign; branch: Branch; draftCampaignId: string }) => void;
  reset: () => void;
};

export const useDraftStore = create<State & Actions>()(
  persist(
    (set) => ({
      draft: initialDraft,
      branch: initialBranch,
      autofillState: "idle",
      activeChatBloggerId: null,
      activeCampaignId: null,
      draftCampaignId: null,
      patchDraft: (patch) => set((s) => ({ draft: { ...s.draft, ...patch } })),
      setBranch: (key, value) => set((s) => ({ branch: { ...s.branch, [key]: value } })),
      toggleBlogger: (id) =>
        set((s) => {
          const removing = s.draft.selectedBloggerIds.includes(id);
          const newFormats = { ...s.draft.bloggerFormats };
          if (removing) delete newFormats[id];
          return {
            draft: {
              ...s.draft,
              selectedBloggerIds: removing
                ? s.draft.selectedBloggerIds.filter((x) => x !== id)
                : [...s.draft.selectedBloggerIds, id],
              bloggerFormats: newFormats,
            },
          };
        }),
      addBloggerFormat: (bloggerId, sel) =>
        set((s) => {
          const existing = s.draft.bloggerFormats[bloggerId] ?? [];
          if (existing.some((x) => x.platform === sel.platform && x.type === sel.type)) return s;
          const next = [...existing, sel];
          const alreadySelected = s.draft.selectedBloggerIds.includes(bloggerId);
          return {
            draft: {
              ...s.draft,
              bloggerFormats: { ...s.draft.bloggerFormats, [bloggerId]: next },
              selectedBloggerIds: alreadySelected
                ? s.draft.selectedBloggerIds
                : [...s.draft.selectedBloggerIds, bloggerId],
            },
          };
        }),
      removeBloggerFormat: (bloggerId, platform, type) =>
        set((s) => {
          const existing = s.draft.bloggerFormats[bloggerId] ?? [];
          const next = existing.filter((x) => !(x.platform === platform && x.type === type));
          const newFormats = { ...s.draft.bloggerFormats, [bloggerId]: next };
          const newIds = next.length === 0
            ? s.draft.selectedBloggerIds.filter((x) => x !== bloggerId)
            : s.draft.selectedBloggerIds;
          return {
            draft: {
              ...s.draft,
              bloggerFormats: newFormats,
              selectedBloggerIds: newIds,
            },
          };
        }),
      assignCreative: (bloggerId, variantId) =>
        set((s) => ({
          draft: {
            ...s.draft,
            creativeChannelMap: { ...s.draft.creativeChannelMap, [bloggerId]: variantId },
          },
        })),
      updateBloggerFormat: (bloggerId, platform, type, entry) =>
        set((s) => {
          const existing = s.draft.bloggerFormats[bloggerId] ?? [];
          const updated = existing.map((x) =>
            x.platform === platform && x.type === type
              ? { ...x, formatId: entry.id, formatName: entry.name, price: entry.price, cpv: entry.cpv }
              : x
          );
          return { draft: { ...s.draft, bloggerFormats: { ...s.draft.bloggerFormats, [bloggerId]: updated } } };
        }),
      setBloggerUtm: (bloggerId, utm) =>
        set((s) => ({
          draft: {
            ...s.draft,
            utmPerBlogger: { ...s.draft.utmPerBlogger, [bloggerId]: utm },
          },
        })),
      setPlacementDate: (bloggerId, date, dateTo) =>
        set((s) => ({
          draft: {
            ...s.draft,
            placementDates: { ...s.draft.placementDates, [bloggerId]: { date, date_to: dateTo } },
          },
        })),
      setBudget: (n) => set((s) => ({ draft: { ...s.draft, budget: n } })),
      setGeo: (geo) => set((s) => ({ draft: { ...s.draft, geo } })),
      setVariants: (variants) => set((s) => ({ draft: { ...s.draft, variants } })),
      setAutofillState: (autofillState) => set({ autofillState }),
      setActiveChat: (id) => set({ activeChatBloggerId: id }),
      setActiveCampaign: (id) => set({ activeCampaignId: id }),
      setDraftCampaignId: (draftCampaignId) => set({ draftCampaignId }),
      loadDraftFromServer: ({ draft, branch, draftCampaignId }) =>
        set({ draft: { ...initialDraft, ...draft }, branch: { ...initialBranch, ...branch }, draftCampaignId, autofillState: "done" }),
      reset: () =>
        set({
          draft: initialDraft,
          branch: initialBranch,
          autofillState: "idle",
          draftCampaignId: null,
        }),
    }),
    {
      name: "lk2-draft-v3",
      // Merge persisted state on top of the initial state so any new fields
      // added in code (e.g. draft.variants) start as their defaults instead of
      // `undefined` from older localStorage snapshots.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<State>;
        return {
          ...current,
          ...p,
          draft: { ...current.draft, ...(p.draft ?? {}) },
          branch: { ...current.branch, ...(p.branch ?? {}) },
        };
      },
    },
  ),
);
